/**
 * Centralized error handlers for TrustGrid pages
 * Handles cross-org access and other common errors
 */

/**
 * Handle cross-org project access errors (404)
 * Silently clears invalid project and redirects to projects page
 *
 * @param {Error} error - Axios error object
 * @param {Function} selectProject - TrustGrid context selectProject function
 * @param {Function} navigate - React Router navigate function
 * @param {string} pageName - Name of page for logging (e.g., 'Connectors', 'Dashboard')
 * @returns {boolean} - True if handled as cross-org error, false otherwise
 */
export const handleCrossOrgError = (error, selectProject, navigate, pageName = 'Page') => {
  // 404 = cross-org access (project doesn't belong to user's org)
  if (error.response?.status === 404) {
    console.warn(`[${pageName}] Project not accessible (cross-org), redirecting`);
    selectProject(null);  // Clear invalid project from context
    navigate('/trustgrid/projects');  // Redirect to projects page
    return true;  // Error was handled
  }
  return false;  // Error not handled, caller should show error
};

/**
 * Standard fetch data handler for TrustGrid pages
 * Wraps API calls with cross-org error handling
 *
 * @param {Function} fetchFn - Async function that fetches data
 * @param {Object} options - Options object
 * @param {Function} options.selectProject - TrustGrid context selectProject
 * @param {Function} options.navigate - React Router navigate
 * @param {string} options.pageName - Page name for logging
 * @param {Function} options.onError - Optional custom error handler
 * @returns {Promise} - Promise that resolves to fetch result or throws
 */
export const handleTrustGridFetch = async (fetchFn, options = {}) => {
  const {
    selectProject,
    navigate,
    pageName = 'TrustGrid Page',
    onError
  } = options;

  try {
    return await fetchFn();
  } catch (error) {
    // Try to handle as cross-org error first
    const wasHandled = handleCrossOrgError(error, selectProject, navigate, pageName);

    if (!wasHandled) {
      // Not a cross-org error, use custom handler or re-throw
      if (onError) {
        onError(error);
      } else {
        throw error;
      }
    }
  }
};

export default {
  handleCrossOrgError,
  handleTrustGridFetch
};
